import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { LOCALE_LABELS, SUPPORTED_LOCALES, type SupportedLocale } from '../i18n'

// Inline SVG rather than emoji: Windows renders flag emoji as two letters,
// and an icon-set fetch would need a CSP exception on a public page.
const FLAGS: Record<SupportedLocale, ReactNode> = {
  fi: (
    <svg viewBox="0 0 18 11" aria-hidden="true">
      <rect width="18" height="11" fill="#fff" />
      <rect x="5" width="3" height="11" fill="#002f6c" />
      <rect y="4" width="18" height="3" fill="#002f6c" />
    </svg>
  ),
  sv: (
    <svg viewBox="0 0 16 10" aria-hidden="true">
      <rect width="16" height="10" fill="#006aa7" />
      <rect x="5" width="2" height="10" fill="#fecc00" />
      <rect y="4" width="16" height="2" fill="#fecc00" />
    </svg>
  ),
  en: (
    <svg viewBox="0 0 60 30" aria-hidden="true">
      <clipPath id="jupi-flag-gb">
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#jupi-flag-gb)" stroke="#c8102e" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#c8102e" strokeWidth="6" />
    </svg>
  ),
}

export function Flag({ locale }: { locale: SupportedLocale }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        width: 24,
        height: 16,
        flexShrink: 0,
        borderRadius: '2px',
        overflow: 'hidden',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
        '& svg': { width: '100%', height: '100%', display: 'block' },
      }}
    >
      {FLAGS[locale]}
    </Box>
  )
}

const current = (language: string): SupportedLocale =>
  (SUPPORTED_LOCALES as readonly string[]).includes(language.slice(0, 2))
    ? (language.slice(0, 2) as SupportedLocale)
    : 'fi'

// A controlled flag-and-name picker: the language field on a profile.
export function LocaleField({
  value,
  onChange,
  label,
  size,
}: {
  value: string
  onChange: (locale: SupportedLocale) => void
  label?: string
  size?: 'small' | 'medium'
}) {
  return (
    <TextField
      select
      size={size}
      label={label}
      value={current(value)}
      onChange={(e) => onChange(e.target.value as SupportedLocale)}
      slotProps={{
        select: {
          renderValue: (v) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Flag locale={v as SupportedLocale} />
              {LOCALE_LABELS[v as SupportedLocale]}
            </Box>
          ),
        },
      }}
      sx={{ minWidth: 150 }}
    >
      {SUPPORTED_LOCALES.map((locale) => (
        <MenuItem key={locale} value={locale} lang={locale} sx={{ gap: 1.5 }}>
          <Flag locale={locale} />
          {LOCALE_LABELS[locale]}
        </MenuItem>
      ))}
    </TextField>
  )
}

// For pages without a signed-in member (whose locale would otherwise come
// from their profile). The choice sticks via i18next's localStorage cache.
export function LanguageSelector({ label }: { label?: string }) {
  const { i18n } = useTranslation()
  return <LocaleField size="small" label={label} value={i18n.language} onChange={(l) => void i18n.changeLanguage(l)} />
}
